import { useEffect, useState } from "react";
import { api, eur, frDate, openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown } from "lucide-react";

const empty = {
  reference: "", person_name: "", role: "bénévole",
  purpose: "", location: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date().toISOString().slice(0, 10),
  transport: "", allowance: 0,
  issue_date: new Date().toISOString().slice(0, 10),
  notes: "",
};

export default function OrdresMission() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/missions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.person_name || !form.purpose) { toast.error("Personne et objet requis"); return; }
    try {
      const payload = { ...form, allowance: Number(form.allowance) || 0,
        reference: form.reference || `OM-${Date.now().toString().slice(-6)}`,
      };
      if (edit) await api.put(`/missions/${edit.id}`, payload);
      else await api.post("/missions", payload);
      toast.success(edit ? "Ordre modifié." : "Ordre de mission créé.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (m) => {
    if (!window.confirm("Supprimer cet ordre de mission ?")) return;
    await api.delete(`/missions/${m.id}`); load();
  };

  return (
    <div data-testid="ordres-mission-page">
      <PageHeader
        eyebrow="Mobilité"
        title="Ordres de mission"
        description="Formalisez les déplacements des bénévoles et salariés en mission pour l'association."
        actions={
          <Button data-testid="new-mission-btn" onClick={() => { setEdit(null); setForm({ ...empty, reference: `OM-${Date.now().toString().slice(-6)}` }); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <Plus size={16} className="mr-1" /> Nouvel ordre
          </Button>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 small-caps">
            <tr>
              <th className="text-left py-3 px-4">Référence</th>
              <th className="text-left py-3 px-4">Personne</th>
              <th className="text-left py-3 px-4">Objet</th>
              <th className="text-left py-3 px-4">Lieu</th>
              <th className="text-left py-3 px-4">Dates</th>
              <th className="text-right py-3 px-4">Indemnité</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-gray-400">Aucun ordre de mission.</td></tr>}
            {rows.map((m) => (
              <tr key={m.id} className="border-t border-brand-border hover:bg-gray-50">
                <td className="py-3 px-4 font-mono text-xs">{m.reference}</td>
                <td className="py-3 px-4 text-gray-900">{m.person_name}<div className="text-xs text-gray-400">{m.role}</div></td>
                <td className="py-3 px-4 text-gray-700 max-w-xs truncate">{m.purpose}</td>
                <td className="py-3 px-4 text-gray-700">{m.location}</td>
                <td className="py-3 px-4 text-gray-700 text-xs">Du {frDate(m.start_date)}<br/>au {frDate(m.end_date)}</td>
                <td className="py-3 px-4 text-right text-brand-navy">{eur(m.allowance)}</td>
                <td className="py-3 px-4 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/missions/${m.id}/pdf`)}><FileDown size={14} className="mr-1" />PDF</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(m); setForm({ ...empty, ...m }); setOpen(true); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(m)}><Trash2 size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier" : "Nouvel ordre de mission"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div><Label>Référence</Label><Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} /></div>
            <div><Label>Date d'émission</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({...form, issue_date: e.target.value})} /></div>
            <div><Label>Nom complet *</Label><Input value={form.person_name} onChange={(e) => setForm({...form, person_name: e.target.value})} /></div>
            <div><Label>Qualité</Label><Input value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} placeholder="bénévole, salarié..." /></div>
            <div className="md:col-span-2"><Label>Objet de la mission *</Label><Textarea rows={2} value={form.purpose} onChange={(e) => setForm({...form, purpose: e.target.value})} /></div>
            <div><Label>Lieu</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} /></div>
            <div><Label>Moyen de transport</Label><Input value={form.transport} onChange={(e) => setForm({...form, transport: e.target.value})} placeholder="Train, voiture personnelle..." /></div>
            <div><Label>Date de départ</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} /></div>
            <div><Label>Date de retour</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} /></div>
            <div><Label>Indemnités (€)</Label><Input type="number" step="0.01" value={form.allowance} onChange={(e) => setForm({...form, allowance: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Observations</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
